import { useState } from 'react';
import { api, type VendorRow } from '../api';
import { jobsApi, type AbandonedCartsData, type PriceComparisonData, type ReportCamp, type SumData, type VelocityData } from '../jobsApi';
import { money, ts, unit, useDebounced, useLoad } from '../lib';

// Staff reports — the report screens Adobe's admin had (commerce server/src/routes/reports.ts).
// Each shows a preview here and downloads the full file. Adobe queued Velocity and Price Comparison
// and posted an inbox notice when the file was ready; these build when asked.

type Tab = 'velocity' | 'price' | 'sum' | 'carts';
const TABS: { id: Tab; label: string }[] = [
  { id: 'velocity', label: 'Velocity' }, { id: 'price', label: 'Vendor Price Comparison' },
  { id: 'sum', label: 'SUM (Spending Update Memo)' }, { id: 'carts', label: 'Abandoned Carts' },
];

const thisYear = new Date().getFullYear();
const today = new Date().toISOString().slice(0, 10);

/** Pick camps (multi) or one camp, by name. */
function CampPicker({ picked, onChange, single }: { picked: ReportCamp[]; onChange: (c: ReportCamp[]) => void; single?: boolean }) {
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const { data } = useLoad(() => (q.length >= 2 ? jobsApi.camps(q) : Promise.resolve([])), [q]);
  const add = (c: ReportCamp) => { onChange(single ? [c] : [...picked.filter((p) => p.id !== c.id), c]); setText(''); };
  return (
    <div className="field">
      <span className="inline-field">{single ? 'Camp' : 'Camps (none = every active camp)'}</span>
      <input className="search" placeholder="Type a camp name…" value={text} onChange={(e) => setText(e.target.value)} />
      {data && data.length > 0 && text && (
        <div className="typeahead">{data.slice(0, 12).map((c) => <button key={c.id} className="ta-opt" onClick={() => add(c)}>{c.name}</button>)}</div>
      )}
      {picked.length > 0 && (
        <div className="picked-row">{picked.map((c) => (
          <span key={c.id} className="invite-chip">{c.name} <button className="link" aria-label={`Remove ${c.name}`} onClick={() => onChange(picked.filter((p) => p.id !== c.id))}>×</button></span>
        ))}</div>
      )}
    </div>
  );
}

function VendorPicker({ picked, onChange, label }: { picked: VendorRow[]; onChange: (v: VendorRow[]) => void; label: string }) {
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const { data } = useLoad(() => (q.length >= 2 ? api.commerceVendors(q) : Promise.resolve([])), [q]);
  return (
    <div className="field">
      <span className="inline-field">{label}</span>
      <input className="search" placeholder="Type a vendor name…" value={text} onChange={(e) => setText(e.target.value)} />
      {data && data.length > 0 && text && (
        <div className="typeahead">{data.slice(0, 12).map((v) => (
          <button key={v.id} className="ta-opt" onClick={() => { onChange([...picked.filter((p) => p.id !== v.id), v]); setText(''); }}>{v.name}</button>
        ))}</div>
      )}
      {picked.length > 0 && (
        <div className="picked-row">{picked.map((v) => (
          <span key={v.id} className="invite-chip">{v.name} <button className="link" aria-label={`Remove ${v.name}`} onClick={() => onChange(picked.filter((p) => p.id !== v.id))}>×</button></span>
        ))}</div>
      )}
    </div>
  );
}

/** Run a preview; show its error inline. */
function useReport<T>() {
  const [data, setData] = useState<T | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<T>) => {
    setBusy(true); setErr(null);
    try { setData(await fn()); } catch (e) { setErr((e as Error).message); setData(null); } finally { setBusy(false); }
  };
  return { data, err, busy, run };
}

function Velocity() {
  const [from, setFrom] = useState(`${thisYear}-01-01`);
  const [to, setTo] = useState(today);
  const [filterBy, setFilterBy] = useState<'order' | 'delivery'>('order');
  const [camps, setCamps] = useState<ReportCamp[]>([]);
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [prefixes, setPrefixes] = useState('');
  const [consolidated, setConsolidated] = useState(true);
  const r = useReport<VelocityData>();
  const params = { from, to, filterBy, orgIds: camps.map((c) => c.id).join(','), vendorIds: vendors.map((v) => v.id).join(','), skuPrefixes: prefixes, consolidated: consolidated ? '1' : '0' };
  return (
    <>
      <section className="card">
        <div className="report-form">
          <label className="field"><span className="inline-field">From</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="field"><span className="inline-field">To (inclusive)</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
          <label className="field"><span className="inline-field">Filter by</span>
            <select className="sel" value={filterBy} onChange={(e) => setFilterBy(e.target.value as 'order' | 'delivery')}>
              <option value="order">Order date</option><option value="delivery">Delivery date</option>
            </select></label>
          <label className="field"><span className="inline-field">Master SKU prefixes</span>
            <input className="search short" placeholder="hf, hk" value={prefixes} onChange={(e) => setPrefixes(e.target.value)} /></label>
          <label className="chk"><input type="checkbox" checked={consolidated} onChange={(e) => setConsolidated(e.target.checked)} /> Consolidated (one sheet)</label>
        </div>
        <div className="report-form" style={{ marginTop: 12 }}>
          <CampPicker picked={camps} onChange={setCamps} />
          <VendorPicker picked={vendors} onChange={setVendors} label="Vendors (none = all)" />
        </div>
        <div className="toolbar" style={{ marginTop: 12 }}>
          <button className="btn-sm" disabled={r.busy} onClick={() => r.run(() => jobsApi.report('velocity', params))}>{r.busy ? 'Running…' : 'Preview'}</button>
          <a className="btn-sm ghost" href={jobsApi.exportUrl('velocity', params)}>Download .xlsx</a>
          <span className="muted">{consolidated ? 'One sheet across the chosen camps.' : 'One sheet per camp.'}</span>
        </div>
      </section>
      {r.err && <p className="msg bad">⚠ {r.err}</p>}
      {r.data && (
        <section className="card report-scroll">
          <p className="muted">{r.data.rows.length}{r.data.truncated ? '+ (preview shows the top 500 by received total — the download has every row)' : ''} rows</p>
          <table>
            <thead><tr>{!r.data.consolidated && <th>Camp</th>}<th>Product type</th><th>Item SKU</th><th>Item</th><th>Pack</th><th className="num">Qty ordered</th><th className="num">Qty received</th><th className="num">Received total</th><th>Vendor item #</th><th>Vendor</th></tr></thead>
            <tbody>{r.data.rows.map((x, i) => (
              <tr key={i}>{!r.data!.consolidated && <td>{x.camp}</td>}<td>{x.product_type ?? '—'}</td>
                <td className="mono">{x.master_sku ?? '—'}</td><td>{x.master_name}</td><td className="mono">{x.pack}</td>
                <td className="num">{Number(x.qty_ordered).toLocaleString()}</td><td className="num">{Number(x.qty_received).toLocaleString()}</td>
                <td className="num">{money(Number(x.received_total))}</td><td className="mono">{x.supplier_sku}</td><td>{x.vendor}</td></tr>
            ))}</tbody>
          </table>
        </section>
      )}
    </>
  );
}

function PriceComparison() {
  const [year, setYear] = useState(String(thisYear));
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [types, setTypes] = useState('');
  const r = useReport<PriceComparisonData>();
  const params = { year, vendorIds: vendors.map((v) => v.id).join(','), types };
  const items = r.data ? [...new Map(r.data.rows.map((x) => [x.item_id, x])).values()] : [];
  return (
    <>
      <section className="card">
        <div className="report-form">
          <label className="field"><span className="inline-field">Year</span>
            <select className="sel" value={year} onChange={(e) => setYear(e.target.value)}>
              {Array.from({ length: thisYear - 2016 }, (_, i) => thisYear - i).map((y) => <option key={y}>{y}</option>)}
            </select></label>
          <label className="field"><span className="inline-field">Product types (blank = all)</span>
            <input className="search short" placeholder="Food, Medical" value={types} onChange={(e) => setTypes(e.target.value)} /></label>
          <VendorPicker picked={vendors} onChange={setVendors} label="Vendors to compare (up to 12)" />
        </div>
        <div className="toolbar" style={{ marginTop: 12 }}>
          <button className="btn-sm" disabled={r.busy || vendors.length === 0} onClick={() => r.run(() => jobsApi.report('price-comparison', params))}>{r.busy ? 'Running…' : 'Preview'}</button>
          <a className={`btn-sm ghost${vendors.length === 0 ? ' disabled' : ''}`} aria-disabled={vendors.length === 0} href={vendors.length ? jobsApi.exportUrl('price-comparison', params) : undefined}>Download .xlsx</a>
          {vendors.length === 0 && <span className="muted">Choose at least one vendor.</span>}
        </div>
      </section>
      {r.err && <p className="msg bad">⚠ {r.err}</p>}
      {r.data && (
        <section className="card report-scroll">
          <p className="muted">{r.data.items.toLocaleString()} master items{r.data.truncated ? ' (preview shows 500 — the download has all)' : ''}. Highlighted: lowest unit price.</p>
          <table>
            <thead><tr><th>Master SKU</th><th>Item</th><th className="num">Qty {year}</th><th className="num">Total</th>{r.data.vendors.map((v) => <th key={v} className="num">{v}</th>)}</tr></thead>
            <tbody>{items.map((it) => (
              <tr key={it.item_id}><td className="mono">{it.sku}</td><td>{it.name}</td><td className="num">{it.qty || '—'}</td><td className="num">{it.total ? money(it.total) : '—'}</td>
                {r.data!.vendors.map((v) => {
                  const o = r.data!.rows.find((x) => x.item_id === it.item_id && x.vendor === v);
                  return <td key={v} className={`num${o?.is_best ? ' report-best-cell' : ''}`}>{o?.price != null ? <>{money(o.price)}<div className="muted mono">{unit(o.unit_price)}/u · {o.pack}</div></> : '—'}</td>;
                })}</tr>
            ))}</tbody>
          </table>
        </section>
      )}
    </>
  );
}

function Sum() {
  const [camp, setCamp] = useState<ReportCamp[]>([]);
  const [all, setAll] = useState(false);
  const [asOf, setAsOf] = useState(today);
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const r = useReport<SumData>();
  const params = { orgId: all ? 'all' : camp[0]?.id, asOf, vendorIds: vendors.map((v) => v.id).join(',') };
  const ready = all || camp.length === 1;
  return (
    <>
      <section className="card">
        <div className="report-form">
          {!all && <CampPicker picked={camp} onChange={setCamp} single />}
          <label className="chk"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> All camps (totals per camp)</label>
          <label className="field"><span className="inline-field">Report on date</span><input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} /></label>
          <VendorPicker picked={vendors} onChange={setVendors} label="Vendors (none = all)" />
        </div>
        <div className="toolbar" style={{ marginTop: 12 }}>
          <button className="btn-sm" disabled={r.busy || !ready} onClick={() => r.run(() => jobsApi.report('sum', params))}>{r.busy ? 'Running…' : 'Show'}</button>
          <a className="btn-sm ghost" aria-disabled={!ready} href={ready ? jobsApi.exportUrl('sum', params) : undefined}>Download .xlsx</a>
          <span className="muted">Packaged invoices in locked packages, by GL code — the memo camps see.</span>
        </div>
      </section>
      {r.err && <p className="msg bad">⚠ {r.err}</p>}
      {r.data && (
        <section className="card report-scroll">
          {r.data.camp && <h2>{r.data.camp.name}</h2>}
          <table>
            <thead><tr><th>{r.data.camp ? 'GL code' : 'Camp'}</th><th className="num">{r.data.columns.ytd}</th><th className="num">{r.data.columns.py1}</th><th className="num">{r.data.columns.py2}</th></tr></thead>
            <tbody>
              {r.data.rows.map((x, i) => <tr key={i}><td>{x.label}</td><td className="num">{money(x.ytd)}</td><td className="num">{money(x.py1)}</td><td className="num">{money(x.py2)}</td></tr>)}
              <tr className="report-total"><td>TOTAL</td><td className="num">{money(r.data.total.ytd)}</td><td className="num">{money(r.data.total.py1)}</td><td className="num">{money(r.data.total.py2)}</td></tr>
            </tbody>
          </table>
          <p className="muted">Not audited by an accountant.</p>
        </section>
      )}
    </>
  );
}

function Carts() {
  const { data, err } = useLoad(() => jobsApi.report<AbandonedCartsData>('abandoned-carts', {}));
  return (
    <section className="card report-scroll">
      <div className="toolbar"><a className="btn-sm ghost" href={jobsApi.exportUrl('abandoned-carts', {})}>Download .csv</a>
        {data && <span className="muted">{data.note} Carts untouched 15 minutes and younger than {data.lifetimeDays} days.</span>}</div>
      {err && <p className="msg bad">⚠ {err}</p>}
      {data && (
        <table>
          <thead><tr><th>Customer</th><th>Camp</th><th className="num">Products</th><th className="num">Qty</th><th className="num">Subtotal</th><th>Created</th><th>Updated</th></tr></thead>
          <tbody>
            {data.rows.map((c) => (
              <tr key={c.id}><td>{c.display_name ?? '—'}<div className="muted mono">{c.email}{c.role && c.role !== 'buyer' ? ` · ${c.role}` : ''}</div></td><td>{c.camp}</td>
                <td className="num">{c.lines}</td><td className="num">{Number(c.qty ?? 0)}</td><td className="num">{money(c.subtotal)}</td>
                <td className="mono">{ts(c.created_at)}</td><td className="mono">{ts(c.updated_at)}</td></tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={7} className="loading">No abandoned carts.</td></tr>}
          </tbody>
        </table>
      )}
      {!data && !err && <p className="loading">Loading…</p>}
    </section>
  );
}

export function ReportsPanel() {
  const [tab, setTab] = useState<Tab>('velocity');
  return (
    <div className="stack">
      <h1>Reports</h1>
      <p className="sub">Adobe's staff reports — preview here, download the full file.</p>
      <div className="toolbar">{TABS.map((t) => <button key={t.id} className={`chip${tab === t.id ? ' on' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>)}</div>
      {tab === 'velocity' && <Velocity />}
      {tab === 'price' && <PriceComparison />}
      {tab === 'sum' && <Sum />}
      {tab === 'carts' && <Carts />}
    </div>
  );
}
